import type { ReactNode } from 'react';
import { createContext, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useLocalStorageByUser from 'common/hooks/useLocalStorageByUser';
import GuidedTour from './GuidedTour';
import type {
  GuidedTourContextValue,
  GuidedTourDefinition,
  GuidedTourRegistryItem,
  GuidedTourStatus,
} from './GuidedTour.types';
import {
  EMPTY_GUIDED_TOUR_STORED_STATE,
  getFirstVisibleStepIndex,
  getNextGuidedTourStatusState,
  getNextVisibleStepIndex,
  getPreviousVisibleStepIndex,
  GUIDED_TOUR_STORAGE_KEY,
  normalizeGuidedTourStoredState,
} from './GuidedTour.utils';

export const GuidedTourContext = createContext<GuidedTourContextValue | null>(null);

interface GuidedTourProviderProps {
  children: ReactNode;
}

const GUIDED_TOUR_ROUTE_WAIT_TIMEOUT_MS = 2000;

const GuidedTourProvider = ({ children }: GuidedTourProviderProps) => {
  const navigate = useNavigate();
  const [registry, setRegistry] = useState<GuidedTourRegistryItem[]>([]);
  const [activeTour, setActiveTour] = useState<GuidedTourDefinition | null>(null);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [status, setStatus] = useState<GuidedTourStatus>('idle');
  const [storedTourStateValue, setStoredTourState, isStoredTourStateReady] = useLocalStorageByUser(
    GUIDED_TOUR_STORAGE_KEY,
    EMPTY_GUIDED_TOUR_STORED_STATE
  );
  const storedTourState = useMemo(
    () => normalizeGuidedTourStoredState(storedTourStateValue),
    [storedTourStateValue]
  );
  const storedTourStatuses = storedTourState.statuses;

  const availableTours = useMemo<GuidedTourRegistryItem[]>(() => {
    if (!isStoredTourStateReady) {
      return registry.map(item => ({ ...item, isRecommended: false }));
    }

    const recommendedTour = registry.find(
      item => item.isRecommended && !storedTourStatuses[item.definition.id]
    ) ?? registry.find(item => !storedTourStatuses[item.definition.id]);

    return registry.map(item => ({
      ...item,
      isRecommended: Boolean(recommendedTour && recommendedTour.definition.id === item.definition.id),
    }));
  }, [isStoredTourStateReady, registry, storedTourStatuses]);

  const waitForStepDelay = useCallback((delayMs: number) =>
    new Promise<void>(resolve => {
      window.setTimeout(resolve, delayMs);
    }), []);

  const waitForRoute = useCallback((route: string) =>
    new Promise<void>(resolve => {
      if (typeof window === 'undefined') {
        resolve();
        return;
      }

      const targetUrl = new URL(route, window.location.origin);
      const startedAt = window.performance.now();

      const hasReachedRoute = () =>
        window.location.pathname === targetUrl.pathname
        && (!targetUrl.search || window.location.search === targetUrl.search)
        && (!targetUrl.hash || window.location.hash === targetUrl.hash);

      const checkRoute = () => {
        if (hasReachedRoute() || window.performance.now() - startedAt >= GUIDED_TOUR_ROUTE_WAIT_TIMEOUT_MS) {
          resolve();
          return;
        }

        window.requestAnimationFrame(checkRoute);
      };

      checkRoute();
    }), []);

  const registerTours = useCallback((tours: GuidedTourRegistryItem[]) => {
    setRegistry(currentTours => {
      const nextToursById = new Map(currentTours.map(item => [item.definition.id, item]));
      tours.forEach(item => nextToursById.set(item.definition.id, item));

      return Array.from(nextToursById.values());
    });

    return () => {
      setRegistry(currentTours =>
        currentTours.filter(item => !tours.some(tour => tour.definition.id === item.definition.id))
      );
    };
  }, []);

  const runBeforeStep = useCallback(async (tour: GuidedTourDefinition, stepIndex: number) => {
    const step = tour.steps[stepIndex];

    if (step?.beforeStepDelayMs) {
      await waitForStepDelay(step.beforeStepDelayMs);
    }

    await step?.beforeStep?.();
  }, [waitForStepDelay]);

  const runAfterStep = useCallback(async (tour: GuidedTourDefinition, stepIndex: number) => {
    await tour.steps[stepIndex]?.afterStep?.();
  }, []);

  const moveToStep = useCallback(
    async (tour: GuidedTourDefinition, stepIndex: number) => {
      const step = tour.steps[stepIndex];

      if (step?.route) {
        navigate(step.route);
        await waitForRoute(step.route);
      }

      setActiveStepIndex(stepIndex);
      await runBeforeStep(tour, stepIndex);
    },
    [navigate, runBeforeStep, waitForRoute]
  );

  const startTour = useCallback(
    (tourId: string) => {
      const tour = registry.find(item => item.definition.id === tourId)?.definition;

      if (!tour) {
        return;
      }

      const firstStepIndex = getFirstVisibleStepIndex(tour);

      if (firstStepIndex === -1) {
        return;
      }

      setActiveTour(tour);
      setStatus('running');
      moveToStep(tour, firstStepIndex).catch(() => undefined);
    },
    [moveToStep, registry]
  );

  const dismissTour = useCallback(() => {
    if (activeTour) {
      setStoredTourState(getNextGuidedTourStatusState(storedTourState, activeTour.id, 'dismissed'));
    }

    setActiveTour(null);
    setActiveStepIndex(0);
    setStatus('dismissed');
  }, [activeTour, setStoredTourState, storedTourState]);

  const completeTour = useCallback(() => {
    if (activeTour) {
      setStoredTourState(getNextGuidedTourStatusState(storedTourState, activeTour.id, 'completed'));
    }

    setActiveTour(null);
    setActiveStepIndex(0);
    setStatus('completed');
  }, [activeTour, setStoredTourState, storedTourState]);

  const goToNextStep = useCallback(() => {
    if (!activeTour) {
      return;
    }

    const nextStepIndex = getNextVisibleStepIndex(activeTour, activeStepIndex);

    runAfterStep(activeTour, activeStepIndex).then(() => {
      if (nextStepIndex === -1) {
        completeTour();
        return;
      }

      moveToStep(activeTour, nextStepIndex).catch(() => undefined);
    }).catch(() => undefined);
  }, [activeStepIndex, activeTour, completeTour, moveToStep, runAfterStep]);

  const goToPreviousStep = useCallback(() => {
    if (!activeTour) {
      return;
    }

    const previousStepIndex = getPreviousVisibleStepIndex(activeTour, activeStepIndex);

    if (previousStepIndex === -1) {
      return;
    }

    runAfterStep(activeTour, activeStepIndex).then(() => {
      moveToStep(activeTour, previousStepIndex).catch(() => undefined);
    }).catch(() => undefined);
  }, [activeStepIndex, activeTour, moveToStep, runAfterStep]);

  const contextValue = useMemo<GuidedTourContextValue>(
    () => ({
      activeStepIndex,
      activeTour,
      availableTours,
      completeTour,
      dismissTour,
      goToNextStep,
      goToPreviousStep,
      registerTours,
      startTour,
      status,
    }),
    [
      activeStepIndex,
      activeTour,
      completeTour,
      dismissTour,
      goToNextStep,
      goToPreviousStep,
      registerTours,
      availableTours,
      startTour,
      status,
    ]
  );

  return (
    <GuidedTourContext.Provider value={contextValue}>
      {children}
      <GuidedTour />
    </GuidedTourContext.Provider>
  );
};

export default GuidedTourProvider;
